import { BaseNumber, CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { queryOptions, type UndefinedInitialDataOptions } from '@tanstack/react-query'
import { erc20Abi } from 'viem'
import { Config } from 'wagmi'
import { getBalance, readContract } from 'wagmi/actions'

import { Token } from '../types/Token'
import { fetchPrice } from './fetchPrice'
import { PriceConfig } from './types'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'

export interface TokenDataResult {
  token: Token
  balance: NormalizedNumber | undefined
}

export interface TokenDataQueryOptionsParams {
  token: TokenDefinition
  priceConfig: PriceConfig
  chainId: number
  wagmiConfig: Config
  account: CheckedAddress | undefined
}

export function tokenDataQueryOptions({
  token,
  priceConfig,
  chainId,
  wagmiConfig,
  account,
}: TokenDataQueryOptionsParams): UndefinedInitialDataOptions<TokenDataResult> {
  return queryOptions<TokenDataResult>({
    queryKey: tokenDataQueryKey({ token, account, chainId }),
    queryFn: async () => {
      const unitPriceUsd = await fetchPrice({ token, priceConfig, chainId, wagmiConfig })

      let balance: NormalizedNumber | undefined
      if (account) {
        balance = await fetchBalance({ token, account, chainId, wagmiConfig })
      }

      return { token: new Token({ ...token, unitPriceUsd }), balance }
    },
  })
}

export interface TokenDataQueryKeyParams {
  token: TokenDefinition
  account: CheckedAddress | undefined
  chainId: number
}

export function tokenDataQueryKey({ token, account, chainId }: TokenDataQueryKeyParams): unknown[] {
  return [...getBalancesQueryKeyPrefix({ account, chainId }), 'token', token.address]
}

interface FetchBalanceParams {
  token: TokenDefinition
  account: CheckedAddress
  chainId: number
  wagmiConfig: Config
}

async function fetchBalance({ token, account, chainId, wagmiConfig }: FetchBalanceParams): Promise<NormalizedNumber> {
  if (token.address === CheckedAddress.EEEE()) {
    const { value, decimals } = await getBalance(wagmiConfig, {
      address: account,
      chainId,
    })
    return BaseNumber(value).toNormalizedUnit(decimals)
  }

  const rawBalance = await readContract(wagmiConfig, {
    address: token.address,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [account],
    chainId,
  })
  return token.fromBaseUnit(BaseNumber(rawBalance))
}
